import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./ProductDetailInfo.css";
import { gql } from "apollo-boost";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import {
  PRODUCT_CONDITION_OBJ,
  PRODUCT_GENDER,
  PRODUCT_GENDER_OBJ,
} from "../Upload";
import ProductBrandInfo from "./ProductBrandInfo";
import IconRadioSelect from "../../../assets/icon_radio_select.svg";
import IconRadioUnselect from "../../../assets/icon_radio_unselect.svg";

const GET_CATEGORIES = gql`
  query getCategoriesCached($genders: [Gender!]) {
    getCategoriesCached(genders: $genders, limit: 1000) {
      name
      subcategories {
        id
        name
      }
    }
  }
`;

const GET_SIZE_VALUES = gql`
  query getSizeValues($name: String!) {
    getSizeValues(name: $name) {
      id
      name
    }
  }
`;

/**
 * Upload - 카테고리, 서브 카테고리, 사이즈, 킨디션 선택
 * @param register
 * @param watch
 * @param setValue
 * @param errors
 * @returns {Element}
 * @constructor
 */
const ProductDetailInfo = ({ register, watch, setValue, errors }) => {
  const genderFromForm = watch("gender") || "";
  const categoryFromForm = watch("category") || "";
  const subCategoryFromForm = watch("subCategory") || "";

  const [selectedGender, setSelectedGender] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [sizeValues, setSizeValues] = useState([]);

  const [getCategories, { data, loading, error }] =
    useLazyQuery(GET_CATEGORIES);

  useEffect(() => {
    getCategories({
      variables: {
        genders: selectedGender
          ? [
              selectedGender === "남성"
                ? PRODUCT_GENDER.MEN
                : PRODUCT_GENDER.WOMEN,
              PRODUCT_GENDER.UNISEX,
            ]
          : [PRODUCT_GENDER.UNISEX],
      },
    });
  }, [selectedGender]);

  const allCategories = useMemo(() => {
    if (!data || !data.getCategoriesCached) return [];
    return data.getCategoriesCached;
  }, [data, selectedGender]);

  const allSubCategories = useMemo(() => {
    if (!allCategories || allCategories.length === 0) return [];
    return allCategories.flatMap((category) => category.subcategories);
  }, [allCategories]);

  const allSubCategoryNames = useMemo(() => {
    if (!allSubCategories || allSubCategories.length === 0) return [];
    return allSubCategories.map((sub) => sub.name);
  }, [allSubCategories]);

  const { data: sizeData, loading: sizeLoading } = useQuery(GET_SIZE_VALUES, {
    variables: {
      name:
        selectedSubCategory ||
        (allSubCategoryNames.length > 0 ? allSubCategoryNames[0] : ""),
    },
    skip: !selectedSubCategory && allSubCategoryNames.length === 0,
  });

  // 사이즈 데이터 로드
  useEffect(() => {
    if (sizeData && sizeData.getSizeValues) {
      setSizeValues(sizeData.getSizeValues);
    }
  }, [sizeData]);

  const handleGenderChange = (e) => {
    setSelectedGender(e.target.value);
    setValue("gender", e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setValue("category", e.target.value);
  };

  const handleSubCategoryChange = (e) => {
    setSelectedSubCategory(e.target.value);
    setValue("subCategory", e.target.value);
  };

  useEffect(() => {
    if (genderFromForm && genderFromForm !== selectedGender) {
      setSelectedGender(genderFromForm);
    }

    if (categoryFromForm && categoryFromForm !== selectedCategory) {
      setSelectedCategory(categoryFromForm);
    }

    if (subCategoryFromForm && subCategoryFromForm !== selectedSubCategory) {
      setSelectedSubCategory(subCategoryFromForm);
    }
  }, [
    genderFromForm,
    categoryFromForm,
    subCategoryFromForm,
    selectedGender,
    selectedCategory,
    selectedSubCategory,
  ]);

  const selectedCategoryData = useMemo(() => {
    return allCategories.find((cat) => cat.name === selectedCategory);
  }, [allCategories, selectedCategory]);

  return (
    <div className="ProductDetailInfo-container">
      <div className="ProductDetailInfo-formGroup">
        <div className="input-header">
          <label>성별</label>
        </div>
        <div className="ProductDetailInfo-genderSelector">
          {Object.keys(PRODUCT_GENDER_OBJ).map((label) => (
            <label
              key={label}
              className={`ProductDetailInfo-radioWrap${selectedGender === label ? " checked" : ""}`}
            >
              <input
                type="radio"
                value={label}
                checked={selectedGender === label}
                onChange={handleGenderChange}
                {...register("gender", { required: "성별을 선택해주세요" })}
              />
              <img
                src={
                  selectedGender === label ? IconRadioSelect : IconRadioUnselect
                }
                alt={selectedGender === label ? "선택됨" : "비선택"}
                style={{ width: 28, height: 28, marginRight: 8 }}
              />
              <span className="ProductDetailInfo-radioLabel">{label}</span>
            </label>
          ))}
        </div>
        {errors.gender && (
          <div className="form-footer">
            <p className="error-message">{errors.gender.message}</p>
          </div>
        )}
      </div>

      <div className="ProductDetailInfo-formGroup">
        <div className="ProductDetailInfo-formGroup-header input-header">
          <label>카테고리</label>
        </div>
        {loading ? (
          <div className="ProductDetailInfo-loadingIndicator"></div>
        ) : error ? (
          <div className="ProductDetailInfo-error">
            카테고리를 불러오는데 실패했습니다
            <br />
            {error.message}
          </div>
        ) : (
          allCategories.length > 0 && (
            <div className="common-grid">
              {allCategories.map((category) => (
                <label key={category.name} className="common-option">
                  <input
                    type="radio"
                    value={category.name}
                    checked={selectedCategory === category.name}
                    onChange={handleCategoryChange}
                    disabled={!selectedGender}
                    {...register("category", {
                      required: "카테고리를 선택해주세요",
                    })}
                  />
                  <span
                    className={`common-label ${!selectedGender ? "common-label--disabled" : ""}`}
                  >
                    {category.name}
                  </span>
                </label>
              ))}
            </div>
          )
        )}
        {errors.category && (
          <div className="form-footer">
            <p className="error-message">{errors.category.message}</p>
          </div>
        )}
      </div>

      <div className="ProductDetailInfo-formGroup">
        <div className="ProductDetailInfo-formGroup-header input-header">
          <label>서브 카테고리</label>
        </div>
        {loading ? (
          <div className="ProductDetailInfo-loadingIndicator"></div>
        ) : error ? (
          <div className="ProductDetailInfo-error">
            서브 테고리를 불러오는데 실패했습니다
          </div>
        ) : (
          <div className="common-grid">
            {selectedCategoryData
              ? selectedCategoryData.subcategories.map((sub) => (
                  <label key={sub.id} className="common-option">
                    <input
                      type="radio"
                      value={sub.name}
                      checked={selectedSubCategory === sub.name}
                      onChange={handleSubCategoryChange}
                      disabled={!selectedCategory}
                      {...register("subCategory", {
                        required: "서브카테고리를 선택해주세요",
                      })}
                    />
                    <span
                      className={`common-label ${!selectedCategory ? "common-label--disabled" : ""}`}
                    >
                      {sub.name}
                    </span>
                  </label>
                ))
              : allSubCategories.length > 0 &&
                allSubCategories.slice(0, 10).map((sub) => (
                  <label key={sub.id} className="common-option">
                    <input
                      type="radio"
                      value={sub.name}
                      checked={selectedSubCategory === sub.name}
                      onChange={handleSubCategoryChange}
                      disabled={!selectedCategory}
                      {...register("subCategory", {
                        required: "서브카테고리를 선택해주세요",
                      })}
                    />
                    <span
                      className={`common-label ${!selectedCategory ? "common-label--disabled" : ""}`}
                    >
                      {sub.name}
                    </span>
                  </label>
                ))}
          </div>
        )}
        {errors.subCategory && (
          <div className="form-footer">
            <p className="error-message">{errors.subCategory.message}</p>
          </div>
        )}
      </div>

      <div className="ProductDetailInfo-formGroup">
        <div className="ProductDetailInfo-formGroup-header input-header">
          <label>사이즈</label>
        </div>
        {sizeLoading ? (
          <div className="ProductDetailInfo-loadingIndicator"></div>
        ) : sizeValues.length > 0 ? (
          <div className="common-grid">
            {sizeValues.map((size) => (
              <label key={size.id} className="common-option">
                <input
                  type="radio"
                  value={size.name}
                  disabled={!selectedSubCategory}
                  {...register("size", { required: "사이즈를 선택해주세요" })}
                />
                <span
                  className={`common-label ${!selectedSubCategory ? "common-label--disabled" : ""}`}
                >
                  {size.name}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <div className="ProductDetailInfo-placeholderMessage">
            서브카테고리를 선택하면 사이즈 옵션이 표시됩니다
          </div>
        )}
        {errors.size && (
          <div className="form-footer">
            <p className="error-message">{errors.size.message}</p>
          </div>
        )}
      </div>

      <div className="ProductDetailInfo-formGroup">
        <div className="ProductDetailInfo-formGroup-header input-header">
          <label>상태</label>
        </div>
        <div className="common-grid">
          {Object.keys(PRODUCT_CONDITION_OBJ).map((condition) => (
            <label key={condition} className="common-option">
              <input
                type="radio"
                value={condition}
                disabled={!selectedSubCategory}
                {...register("condition", { required: "상태를 선택해주세요" })}
              />
              <span
                className={`common-label ${!selectedSubCategory ? "common-label--disabled" : ""}`}
              >
                {condition}
              </span>
            </label>
          ))}
        </div>
        {errors.condition && (
          <div className="form-footer">
            <p className="error-message">{errors.condition.message}</p>
          </div>
        )}
      </div>

      <div className="ProductDetailInfo-formGroup">
        <ProductBrandInfo
          register={register}
          watch={watch}
          setValue={setValue}
        />
      </div>
    </div>
  );
};

ProductDetailInfo.propTypes = {
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
};

export default ProductDetailInfo;
